import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  TextField, MenuItem, Box
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useTranslation } from 'react-i18next';
import ObjectivesService from '../../services/ObjectivesService';
import SnackbarUtils from '../../utils/SnackbarUtils';

const CATEGORY_OPTIONS = [
  { value: 'sales', label: 'Ventas' },
  { value: 'tickets', label: 'Tickets' },
  { value: 'clients', label: 'Clientes' },
  { value: 'growth', label: 'Crecimiento' }
];

const AddEditObjectiveModal = ({ open, onClose, onSave, objective }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'sales',
    default_target_value: '' // Cambiado a default_target_value para plantillas
  });

  useEffect(() => {
    if (open) {
      if (objective) {
        // Normalizar targetValue (camelCase del backend) a default_target_value (snake_case usado en frontend)
        const targetValue = objective.targetValue ?? objective.target_value ?? objective.defaultTargetValue ?? objective.default_target_value ?? '';
        setFormData({
          title: objective.title || '',
          description: objective.description || '',
          category: objective.category || 'sales',
          default_target_value: targetValue
        });
      } else {
        setFormData({
          title: '',
          description: '',
          category: 'sales',
          default_target_value: ''
        });
      }
    }
  }, [open, objective]);

  const handleChange = (field) => (event) => {
    setFormData({
      ...formData,
      [field]: event.target.value
    });
  };

  const handleSave = async () => {
    if (!formData.title) {
      SnackbarUtils.error(t('fill_required_fields') || 'Por favor completa todos los campos requeridos');
      return;
    }

    // El default_target_value es opcional (puede ser asignado después)
    let defaultTargetValue = null;
    if (formData.default_target_value) {
      defaultTargetValue = parseFloat(formData.default_target_value);
      if (isNaN(defaultTargetValue) || defaultTargetValue <= 0) {
        SnackbarUtils.error(t('invalid_target_value') || 'El valor objetivo por defecto debe ser un número mayor a 0');
        return;
      }
    }

    try {
      setLoading(true);
      const objectiveData = {
        title: formData.title,
        description: formData.description || '',
        category: formData.category,
        default_target_value: defaultTargetValue // Enviar como default_target_value
      };

      console.log('[AddEditObjectiveModal] Form Data:', formData);
      console.log('[AddEditObjectiveModal] Objective Data to send:', objectiveData);

      if (objective) {
        console.log('[AddEditObjectiveModal] Updating objective ID:', objective.id);
        await ObjectivesService.updateObjective(objective.id, objectiveData);
        SnackbarUtils.success(t('objective_updated') || 'Objetivo actualizado exitosamente');
      } else {
        console.log('[AddEditObjectiveModal] Creating new objective');
        await ObjectivesService.createObjective(objectiveData);
        SnackbarUtils.success(t('objective_created') || 'Objetivo creado exitosamente');
      }

      onSave();
    } catch (error) {
      console.error('[AddEditObjectiveModal] Error saving objective:', error);
      console.error('[AddEditObjectiveModal] Error response:', error.response?.data);
      SnackbarUtils.error(t('error_saving_objective') || 'Error al guardar objetivo');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {objective ? t('edit_objective') || 'Editar Objetivo' : t('add_objective') || 'Agregar Objetivo'}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField
            label={t('title') || 'Título'}
            value={formData.title}
            onChange={handleChange('title')}
            fullWidth
            required
          />
          <TextField
            label={t('description') || 'Descripción'}
            value={formData.description}
            onChange={handleChange('description')}
            fullWidth
            multiline
            rows={3}
          />
          <TextField
            select
            label={t('category') || 'Categoría'}
            value={formData.category}
            onChange={handleChange('category')}
            fullWidth
            required
          >
            {CATEGORY_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label={t('default_target_value') || 'Valor Sugerido (Opcional)'}
            value={formData.default_target_value}
            onChange={handleChange('default_target_value')}
            fullWidth
            type="number"
            helperText={t('default_target_value_help') || 'Este es un valor sugerido que se pre-llenará automáticamente cuando asignes este objetivo. Puedes cambiarlo al momento de asignarlo a Global, Sucursal o Personal.'}
            inputProps={{ min: 0, step: 0.01 }}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('cancel') || 'Cancelar'}</Button>
        <LoadingButton onClick={handleSave} loading={loading} variant="contained">
          {t('save') || 'Guardar'}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default AddEditObjectiveModal;

